import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tabs,
  Tab,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NotesIcon from "@mui/icons-material/Notes";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BadgeIcon from "@mui/icons-material/Badge";
import CategoryIcon from "@mui/icons-material/Category";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GavelIcon from "@mui/icons-material/Gavel";
import LanguageIcon from "@mui/icons-material/Language";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ConvertProspectEntitiesDialog from "./ConvertProspectEntitiesDialog";
import axios from "axios";
import { isGermanyCountry } from "../utils/taxOfficeUtils";

const ViewProspectManagementDialog = ({ open, onClose, prospectId }) => {
  const navigate = useNavigate();
  const [prospectDetails, setProspectDetails] = useState(null);
  const [entities, setEntities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [currentEntityIndex, setCurrentEntityIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteData, setEditNoteData] = useState({ shortDescription: "", noteContent: "" });
  const [editNoteFile, setEditNoteFile] = useState(null);
  const [editNoteFileError, setEditNoteFileError] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [newNoteData, setNewNoteData] = useState({ shortDescription: "", noteContent: "" });
  const [newNoteFile, setNewNoteFile] = useState(null);
  const [newNoteFileError, setNewNoteFileError] = useState("");
  const [deletingNote, setDeletingNote] = useState(false);
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [editingCustomerInfo, setEditingCustomerInfo] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [pdfFileName, setPdfFileName] = useState("");
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [customerInfoData, setCustomerInfoData] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    address: "",
    postalCode: "",
    city: "",
    countryId: "",
  });
  const [savingCustomerInfo, setSavingCustomerInfo] = useState(false);

  useEffect(() => {
    if (open && prospectId) {
      fetchProspectDetails();
      fetchCountries();
      fetchRelationTypes();
      fetchUsers();
    } else {
      // Reset state when dialog closes
      setProspectDetails(null);
      setEntities([]);
      setNotes([]);
      setCurrentEntityIndex(0);
      setCurrentTab(0);
      setSelectedNoteId(null);
      setEditingNote(null);
      setEditNoteData({ shortDescription: "", noteContent: "" });
      setEditNoteFile(null);
      setEditNoteFileError("");
      setAddingNote(false);
      setNewNoteData({ shortDescription: "", noteContent: "" });
      setNewNoteFile(null);
      setNewNoteFileError("");
      setEditingCustomerInfo(false);
      setCustomerInfoData({
        companyName: "",
        contactName: "",
        email: "",
        phone: "",
        address: "",
        postalCode: "",
        city: "",
        countryId: "",
      });
      setConvertDialogOpen(false);
    }
  }, [open, prospectId]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const fetchProspectDetails = async () => {
    if (!prospectId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/prospects/${prospectId}`);
      setProspectDetails(response.data);
      
      // Initialize customer info data
      setCustomerInfoData({
        companyName: response.data.companyName || "",
        contactName: response.data.contactName || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        address: response.data.address || "",
        postalCode: response.data.postalCode || "",
        city: response.data.city || "",
        countryId: response.data.countryId || "",
      });
      
      // Fetch entities if there are entityIds
      if (response.data.entityIds && response.data.entityIds.length > 0) {
        const entityPromises = response.data.entityIds.map((entityId) =>
          axios.get(`/api/v1/entities/${entityId}`).catch(() => null)
        );
        const entityResponses = await Promise.all(entityPromises);
        const fetchedEntities = entityResponses
          .filter((res) => res !== null)
          .map((res) => res.data);
        setEntities(fetchedEntities);
        setCurrentEntityIndex(0);
      } else {
        setEntities([]);
      }
      
      // Fetch notes
      await fetchProspectNotes();
    } catch (error) {
      console.error("Error fetching prospect details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProspectNotes = async () => {
    if (!prospectId) return;
    try {
      const response = await axios.get(`/api/v1/prospectnotes/prospect/${prospectId}`);
      const fetchedNotes = response.data || [];
      setNotes(fetchedNotes);
      // Select first note if available
      if (fetchedNotes.length > 0 && !selectedNoteId) {
        setSelectedNoteId(fetchedNotes[0].id || fetchedNotes[0].noteId);
      }
    } catch (error) {
      console.error("Error fetching prospect notes:", error);
      setNotes([]);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get("/api/v1/countries");
      setCountries(response.data || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchRelationTypes = async () => {
    try {
      const response = await axios.get("/api/v1/relationTypes");
      setRelationTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching relation types:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get("/api/v1/AppUsers");
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const getUserName = (createdByGuid) => {
    if (!createdByGuid) return "";
    // Try to find user by id, userId, or convert GUID to string for comparison
    const user = users.find((u) => {
      const userId = u.id || u.userId;
      return userId === createdByGuid || String(userId) === String(createdByGuid);
    });
    if (user) {
      const fullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
      return fullName || user.userName || user.username || "";
    }
    return "";
  };

  const getCountryName = (countryId) => {
    const country = countries.find((c) => c.countryId === countryId);
    return country ? `${country.countryName}` : "";
  };

  const getRelationTypeName = (relationTypeId) => {
    const type = relationTypes.find((t) => t.relationTypeId === relationTypeId);
    return type ? type.shortDescription : "";
  };

  const isEntityPoland = (entity) => {
    return entity.countryId === 17;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No date";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateString;
    }
  };

  const handleNoteClick = (noteId) => {
    setSelectedNoteId(noteId);
    setEditingNote(null);
    setEditNoteData({ shortDescription: "", noteContent: "" });
  };

  const handleEditNote = (note) => {
    setEditingNote(note.id || note.noteId);
    setEditNoteData({
      shortDescription: note.shortDescription || "",
      noteContent: note.noteContent || note.content || "",
    });
    setEditNoteFile(null);
    setEditNoteFileError("");
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditNoteData({ shortDescription: "", noteContent: "" });
    setEditNoteFile(null);
    setEditNoteFileError("");
  };

  const isPdfFile = (file) => {
    if (!file) return false;
    const fileName = file.name || "";
    const isPdfByName = fileName.toLowerCase().endsWith(".pdf");
    const isPdfByType = file.type === "application/pdf";
    return isPdfByType || isPdfByName;
  };

  const uploadNoteFile = async (file) => {
    if (!file) return null;
    if (!isPdfFile(file)) {
      throw new Error("Only PDF files are allowed.");
    }
    const fileInfo = {
      documentType: 123,
      fileName: file.name,
      mimeType: file.type || "application/pdf",
      fileSize: file.size,
    };
    const response = await axios.post("/api/v1/file", fileInfo);
    const fileId = response.data?.fileId ?? null;
    if (!fileId) {
      throw new Error("No fileId returned from /api/v1/file.");
    }

    const formData = new FormData();
    formData.append("file", file, String(fileId));
    await axios.post("/api/v1/azureblobstorage", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return fileId;
  };

  const handleSaveNote = async () => {
    if (!editingNote || !prospectId) return;
    if (editNoteFileError) return;
    
    setSavingNote(true);
    try {
      let fileId = null;
      if (editNoteFile) {
        fileId = await uploadNoteFile(editNoteFile);
      }

      const updatePayload = {
        prospectId: prospectId,
        shortDescription: editNoteData.shortDescription || "",
        noteContent: editNoteData.noteContent || "",
        ...(fileId ? { fileId } : {}),
      };

      console.log("Updating note with payload:", updatePayload);
      console.log("Note ID:", editingNote);
      
      await axios.put(`/api/v1/prospectnotes/${editingNote}`, updatePayload);
      
      // Refresh notes
      await fetchProspectNotes();
      
      setEditingNote(null);
      setEditNoteData({ shortDescription: "", noteContent: "" });
      setEditNoteFile(null);
      setEditNoteFileError("");
    } catch (error) {
      console.error("Error updating note:", error);
      console.error("Error response:", error.response?.data);
      alert("Error updating note: " + (error.response?.data?.message || error.message));
    } finally {
      setSavingNote(false);
    }
  };

  const handleAddNote = () => {
    setAddingNote(true);
    setSelectedNoteId(null);
    setEditingNote(null);
    setNewNoteData({ shortDescription: "", noteContent: "" });
    setNewNoteFile(null);
    setNewNoteFileError("");
  };

  const handleCancelAddNote = () => {
    setAddingNote(false);
    setNewNoteData({ shortDescription: "", noteContent: "" });
    setNewNoteFile(null);
    setNewNoteFileError("");
    // Select first note if available
    if (notes.length > 0) {
      setSelectedNoteId(notes[0].id || notes[0].noteId);
    }
  };

  const handleSaveNewNote = async () => {
    if (!prospectId) return;
    if (newNoteFileError) return;
    
    setSavingNote(true);
    try {
      const currentDate = new Date();
      const day = String(currentDate.getDate()).padStart(2, '0');
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const year = currentDate.getFullYear();
      const formattedDate = `${day}-${month}-${year}`;

      let fileId = null;
      if (newNoteFile) {
        fileId = await uploadNoteFile(newNoteFile);
      }

      const notePayload = {
        prospectId: prospectId,
        shortDescription: newNoteData.shortDescription || `Note created on ${formattedDate}`,
        noteContent: newNoteData.noteContent,
        ...(fileId ? { fileId } : {}),
      };

      const response = await axios.post("/api/v1/prospectnotes", notePayload);
      
      // Refresh notes
      await fetchProspectNotes();
      
      // Select the newly created note
      const newNoteId = response.data?.id || response.data?.noteId;
      if (newNoteId) {
        setSelectedNoteId(newNoteId);
      }
      
      setAddingNote(false);
      setNewNoteData({ shortDescription: "", noteContent: "" });
      setNewNoteFile(null);
      setNewNoteFileError("");
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Error creating note: " + (error.response?.data?.message || error.message));
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNoteClick = () => {
    if (!selectedNoteId) return;
    const note = notes.find((n) => (n.id || n.noteId) === selectedNoteId);
    setNoteToDelete(note);
    setDeleteNoteDialogOpen(true);
  };

  const handleConfirmDeleteNote = async () => {
    if (!noteToDelete || deletingNote) return;
    
    const noteId = noteToDelete.id || noteToDelete.noteId;
    
    setDeletingNote(true);
    try {
      await axios.delete(`/api/v1/prospectnotes/${noteId}`);
      
      // Refresh notes
      await fetchProspectNotes();
      
      // Select first note if available, otherwise clear selection
      const remainingNotes = notes.filter((n) => (n.id || n.noteId) !== noteId);
      if (remainingNotes.length > 0) {
        setSelectedNoteId(remainingNotes[0].id || remainingNotes[0].noteId);
      } else {
        setSelectedNoteId(null);
      }
      
      setEditingNote(null);
      setEditNoteData({ shortDescription: "", noteContent: "" });
      setDeleteNoteDialogOpen(false);
      setNoteToDelete(null);
    } catch (error) {
      console.error("Error deleting note:", error);
      alert("Error deleting note: " + (error.response?.data?.message || error.message));
    } finally {
      setDeletingNote(false);
    }
  };

  const handleCancelDeleteNote = () => {
    setDeleteNoteDialogOpen(false);
    setNoteToDelete(null);
  };

  const handleOpenPdf = async (fileId) => {
    if (!fileId) return;
    setPdfLoading(true);
    setPdfError("");
    try {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      const fileInfoResponse = await axios.get(`/api/v1/file/${fileId}`);
      const fetchedFileName = fileInfoResponse.data?.fileName || "";
      setPdfFileName(fetchedFileName);
      const response = await axios({
        url: `/api/v1/azureblobstorage/${fileId}`,
        method: "GET",
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(response.data);
      setPdfUrl(objectUrl);
      setPdfDialogOpen(true);
    } catch (error) {
      console.error("Error loading PDF:", error);
      setPdfError("Failed to load PDF document.");
      setPdfFileName("");
      setPdfDialogOpen(true);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdf = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setPdfDialogOpen(false);
    setPdfError("");
    setPdfFileName("");
  };

  const handleEditCustomerInfo = () => {
    setEditingCustomerInfo(true);
  };

  const handleCancelEditCustomerInfo = () => {
    // Reset to original prospect details
    if (prospectDetails) {
      setCustomerInfoData({
        companyName: prospectDetails.companyName || "",
        contactName: prospectDetails.contactName || "",
        email: prospectDetails.email || "",
        phone: prospectDetails.phone || "",
        address: prospectDetails.address || "",
        postalCode: prospectDetails.postalCode || "",
        city: prospectDetails.city || "",
        countryId: prospectDetails.countryId || "",
      });
    }
    setEditingCustomerInfo(false);
  };

  const handleConvertToCustomer = () => {
    if (!prospectId) return;
    setConvertDialogOpen(true);
  };

  const handleSaveCustomerInfo = async () => {
    if (!prospectId) return;
    
    setSavingCustomerInfo(true);
    try {
      const updatePayload = {
        companyName: customerInfoData.companyName,
        countryId: customerInfoData.countryId,
        contactName: customerInfoData.contactName || null,
        email: customerInfoData.email || null,
        phone: customerInfoData.phone || null,
        address: customerInfoData.address || null,
        postalCode: customerInfoData.postalCode || null,
        city: customerInfoData.city || null,
      };

      await axios.put(`/api/v1/prospects/${prospectId}`, updatePayload);
      
      // Refresh prospect details
      await fetchProspectDetails();
      
      setEditingCustomerInfo(false);
    } catch (error) {
      console.error("Error updating prospect:", error);
      alert("Error updating prospect: " + (error.response?.data?.message || error.message));
    } finally {
      setSavingCustomerInfo(false);
    }
  };

  const selectedNote = notes.find((note) => (note.id || note.noteId) === selectedNoteId);

  const handleGoToProspect = (entity) => {
    if (entity) {
      const entityId = entity.entityId || entity.id;
      const entityNumber = entity.entityNumber;
      navigate(`/customers/customeroverview?customerId=${entityId}&customerNumber=${entityNumber}`);
      onClose(); // Close the dialog when navigating
    }
  };

  if (!prospectDetails) {
    return null;
  }

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: { xs: "95vh", sm: "900px" },
            height: { xs: "95vh", sm: "auto" },
            margin: { xs: 1, sm: 2 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5">View Prospect</Typography>
          {prospectDetails && 
           (prospectDetails.active === 1 || prospectDetails.active === true) && 
           prospectDetails.finalized !== 1 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleConvertToCustomer}
              sx={{ ml: 2 }}
            >
              Convert to Customer
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 3 }}>
              <Tab label="Customer Info & Notes" />
              <Tab label={`Entities (${entities.length})`} />
            </Tabs>

            <Box sx={{ minHeight: "600px" }}>
              {currentTab === 0 && (
                <>
                {/* Customer Information Card */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PersonIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Customer Information</Typography>
                      </Box>
                      {!editingCustomerInfo && (
                        <IconButton
                          size="small"
                          onClick={handleEditCustomerInfo}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                      {editingCustomerInfo && (
                        <Box>
                          <IconButton
                            size="small"
                            onClick={handleSaveCustomerInfo}
                            disabled={savingCustomerInfo}
                            color="primary"
                          >
                            {savingCustomerInfo ? <CircularProgress size={20} /> : <SaveIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEditCustomerInfo}
                            color="inherit"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {editingCustomerInfo ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Customer Name"
                            value={customerInfoData.companyName}
                            onChange={(e) => setCustomerInfoData({ ...customerInfoData, companyName: e.target.value })}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Contact Person"
                            value={customerInfoData.contactName}
                            onChange={(e) => setCustomerInfoData({ ...customerInfoData, contactName: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={customerInfoData.email}
                            onChange={(e) => setCustomerInfoData({ ...customerInfoData, email: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Phone"
                            value={customerInfoData.phone}
                            onChange={(e) => setCustomerInfoData({ ...customerInfoData, phone: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Address"
                            value={customerInfoData.address}
                            onChange={(e) => setCustomerInfoData({ ...customerInfoData, address: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Postal Code"
                            value={customerInfoData.postalCode}
                            onChange={(e) => setCustomerInfoData({ ...customerInfoData, postalCode: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="City"
                            value={customerInfoData.city}
                            onChange={(e) => setCustomerInfoData({ ...customerInfoData, city: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth required>
                            <InputLabel>Country</InputLabel>
                            <Select
                              value={customerInfoData.countryId}
                              label="Country"
                              onChange={(e) => setCustomerInfoData({ ...customerInfoData, countryId: e.target.value })}
                            >
                              {countries.map((country) => (
                                <MenuItem key={country.countryId} value={country.countryId}>
                                  {country.countryCode}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Customer Name
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                            {prospectDetails.companyName || <em style={{ color: "#999" }}>Not provided</em>}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <PersonIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Contact Person
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                            {prospectDetails.contactName || <em style={{ color: "#999" }}>Not provided</em>}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <EmailIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Email
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                            {prospectDetails.email || <em style={{ color: "#999" }}>Not provided</em>}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Phone
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                            {prospectDetails.phone || <em style={{ color: "#999" }}>Not provided</em>}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Address
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
                            {prospectDetails.address ? (
                              <>
                                {prospectDetails.address}
                                {prospectDetails.postalCode && `, ${prospectDetails.postalCode}`}
                                {prospectDetails.city && ` ${prospectDetails.city}`}
                                {prospectDetails.countryId && `, ${getCountryName(prospectDetails.countryId)}`}
                              </>
                            ) : (
                              <em style={{ color: "#999" }}>Not provided</em>
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>

                {/* Notes Card */}
                <Card>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <NotesIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Notes ({notes.length})
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddNote}
                        disabled={addingNote || editingNote !== null}
                      >
                        Add Note
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {notes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                        No notes available
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {/* Notes List - Left Side */}
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              height: "400px",
                              overflow: "hidden",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <List 
                              disablePadding 
                              sx={{ 
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                height: "100%",
                              }}
                            >
                              {notes.map((note) => {
                                const noteId = note.id || note.noteId;
                                const isSelected = noteId === selectedNoteId;
                                return (
                                  <ListItem key={noteId} disablePadding>
                                    <ListItemButton
                                      selected={isSelected}
                                      onClick={() => handleNoteClick(noteId)}
                                      sx={{
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        "&.Mui-selected": {
                                          backgroundColor: "primary.light",
                                          color: "primary.contrastText",
                                          "&:hover": {
                                            backgroundColor: "primary.main",
                                          },
                                        },
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Typography variant="subtitle2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                            {note.shortDescription || "Untitled Note"}
                                          </Typography>
                                        }
                                        secondary={
                                          <Typography variant="caption" color={isSelected ? "inherit" : "text.secondary"}>
                                            {formatDate(note.createDate)}
                                            {note.createdBy && getUserName(note.createdBy) && ` - ${getUserName(note.createdBy)}`}
                                          </Typography>
                                        }
                                      />
                                    </ListItemButton>
                                  </ListItem>
                                );
                              })}
                            </List>
                          </Box>
                        </Grid>

                        {/* Note Detail - Right Side */}
                        <Grid item xs={12} md={8}>
                          {addingNote ? (
                            <Box
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 2,
                                minHeight: "400px",
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6">Add New Note</Typography>
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={handleSaveNewNote}
                                    disabled={savingNote || Boolean(newNoteFileError) || !newNoteData.noteContent.trim()}
                                    color="primary"
                                  >
                                    {savingNote ? <CircularProgress size={20} /> : <SaveIcon />}
                                  </IconButton>
                                  <IconButton size="small" onClick={handleCancelAddNote} color="inherit">
                                    <CancelIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                              <TextField
                                fullWidth
                                label="Short Description"
                                value={newNoteData.shortDescription}
                                onChange={(e) =>
                                  setNewNoteData({ ...newNoteData, shortDescription: e.target.value })
                                }
                                sx={{ mb: 2 }}
                                placeholder="Leave empty to use default"
                              />
                              <TextField
                                fullWidth
                                label="Note Content"
                                multiline
                                rows={12}
                                value={newNoteData.noteContent}
                                onChange={(e) =>
                                  setNewNoteData({ ...newNoteData, noteContent: e.target.value })
                                }
                                required
                              />
                              <TextField
                                fullWidth
                                name="notePdfFile"
                                label="PDF Document (optional)"
                                type="file"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ accept: "application/pdf,.pdf" }}
                                onChange={(event) => {
                                  const file = event.currentTarget.files?.[0] || null;
                                  setNewNoteFile(file);
                                  if (file && !isPdfFile(file)) {
                                    setNewNoteFileError("Only PDF files are allowed.");
                                  } else {
                                    setNewNoteFileError("");
                                  }
                                }}
                                error={Boolean(newNoteFileError)}
                                helperText={newNoteFileError}
                                sx={{ mt: 2 }}
                              />
                            </Box>
                          ) : selectedNote ? (
                            <Box
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 2,
                                minHeight: "400px",
                              }}
                            >
                              {editingNote === (selectedNote.id || selectedNote.noteId) ? (
                                <>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6">Edit Note</Typography>
                                    <Box>
                                      <IconButton
                                        size="small"
                                        onClick={handleSaveNote}
                                        disabled={savingNote || Boolean(editNoteFileError)}
                                        color="primary"
                                      >
                                        {savingNote ? <CircularProgress size={20} /> : <SaveIcon />}
                                      </IconButton>
                                      <IconButton size="small" onClick={handleCancelEdit} color="inherit">
                                        <CancelIcon />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  <TextField
                                    fullWidth
                                    label="Short Description"
                                    value={editNoteData.shortDescription}
                                    onChange={(e) =>
                                      setEditNoteData({ ...editNoteData, shortDescription: e.target.value })
                                    }
                                    sx={{ mb: 2 }}
                                  />
                                  <TextField
                                    fullWidth
                                    label="Note Content"
                                    multiline
                                    rows={12}
                                    value={editNoteData.noteContent}
                                    onChange={(e) =>
                                      setEditNoteData({ ...editNoteData, noteContent: e.target.value })
                                    }
                                  />
                                  <TextField
                                    fullWidth
                                    name="notePdfFile"
                                    label="PDF Document (optional)"
                                    type="file"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ accept: "application/pdf,.pdf" }}
                                    onChange={(event) => {
                                      const file = event.currentTarget.files?.[0] || null;
                                      setEditNoteFile(file);
                                      if (file && !isPdfFile(file)) {
                                        setEditNoteFileError("Only PDF files are allowed.");
                                      } else {
                                        setEditNoteFileError("");
                                      }
                                    }}
                                    error={Boolean(editNoteFileError)}
                                    helperText={editNoteFileError}
                                    sx={{ mt: 2 }}
                                  />
                                </>
                              ) : (
                                <>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Box>
                                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        {selectedNote.shortDescription || "Untitled Note"}
                                      </Typography>
                                      {selectedNote.createDate && (
                                        <Typography variant="caption" color="text.secondary">
                                          Created: {formatDate(selectedNote.createDate)}
                                          {selectedNote.createdBy && getUserName(selectedNote.createdBy) && ` - ${getUserName(selectedNote.createdBy)}`}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box>
                                      {selectedNote.fileId ? (
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenPdf(selectedNote.fileId)}
                                          color="primary"
                                          disabled={pdfLoading}
                                          title="Open PDF"
                                        >
                                          <PictureAsPdfIcon />
                                        </IconButton>
                                      ) : null}
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEditNote(selectedNote)}
                                        color="primary"
                                        disabled={deletingNote}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={handleDeleteNoteClick}
                                        color="error"
                                        disabled={deletingNote}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  <Divider sx={{ mb: 2 }} />
                                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                    {selectedNote.noteContent || selectedNote.content || "No content"}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 2,
                                minHeight: "400px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Select a note to view details
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
                </>
              )}

              {currentTab === 1 && (
                <>
                  {/* Entities Card */}
                  {entities.length > 0 ? (
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BusinessIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="h6">
                            Entities ({entities.length})
                          </Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      {/* Pagination Controls */}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <IconButton
                          onClick={() => setCurrentEntityIndex((prev) => Math.max(0, prev - 1))}
                          disabled={currentEntityIndex === 0}
                          aria-label="Previous entity"
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">
                          Entity {currentEntityIndex + 1} of {entities.length}
                        </Typography>
                        <IconButton
                          onClick={() => setCurrentEntityIndex((prev) => Math.min(entities.length - 1, prev + 1))}
                          disabled={currentEntityIndex === entities.length - 1}
                          aria-label="Next entity"
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>

                      {/* Current Entity Display */}
                      {entities[currentEntityIndex] && (() => {
                        const entity = entities[currentEntityIndex];
                        return (
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {entity.name || "Unnamed Entity"}
                              </Typography>
                              <Button
                                variant="contained"
                                color="primary"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => handleGoToProspect(entity)}
                                size="small"
                              >
                                Go to Prospect
                              </Button>
                            </Box>
                            
                            <Grid container spacing={3}>
                              {/* Basic Information Card */}
                              <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center" }}>
                                      <BadgeIcon sx={{ mr: 1, fontSize: 20 }} />
                                      Basic Information
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                      <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <BadgeIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Customer Number
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {entity.entityNumber || <em style={{ color: "#999" }}>Not provided</em>}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <CategoryIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Relation Type
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {getRelationTypeName(entity.typeRelation) || <em style={{ color: "#999" }}>Not provided</em>}
                                        </Typography>
                                      </Grid>
                                      {entity.startupDate && (
                                        <Grid item xs={12}>
                                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                            <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                              Start Date
                                            </Typography>
                                          </Box>
                                          <Typography variant="body1" sx={{ ml: 3 }}>
                                            {(() => {
                                              try {
                                                const date = new Date(entity.startupDate);
                                                return isNaN(date.getTime()) ? entity.startupDate : date.toLocaleDateString();
                                              } catch {
                                                return entity.startupDate;
                                              }
                                            })()}
                                          </Typography>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* Contact Information Card */}
                              <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center" }}>
                                      <PhoneIcon sx={{ mr: 1, fontSize: 20 }} />
                                      Contact Information
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                      <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <EmailIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Email
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {entity.generalEmailAddress || <em style={{ color: "#999" }}>Not provided</em>}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Phone
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {entity.generalPhoneNumber || <em style={{ color: "#999" }}>Not provided</em>}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Address
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {isEntityPoland(entity) ? (
                                            <>
                                              {entity.address || ""}
                                              {entity.houseNumber && ` ${entity.houseNumber}`}
                                              {entity.postalCode && `, ${entity.postalCode}`}
                                              {entity.city && ` ${entity.city}`}
                                              {entity.province && `, ${entity.province}`}
                                              {entity.county && `, ${entity.county}`}
                                              {entity.municipality && `, ${entity.municipality}`}
                                              {entity.postOffice && ` (Post Office: ${entity.postOffice})`}
                                              {entity.countryId && `, ${getCountryName(entity.countryId)}`}
                                            </>
                                          ) : (
                                            <>
                                              {entity.address || ""}
                                              {entity.postalCode && `, ${entity.postalCode}`}
                                              {entity.city && ` ${entity.city}`}
                                              {entity.province && `, ${entity.province}`}
                                              {entity.countryId && `, ${getCountryName(entity.countryId)}`}
                                            </>
                                          )}
                                          {!entity.address && !entity.postalCode && !entity.city && (
                                            <em style={{ color: "#999" }}>Not provided</em>
                                          )}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <LanguageIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Website
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {entity.website ? (
                                            <a href={entity.website} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
                                              {entity.website}
                                            </a>
                                          ) : (
                                            <em style={{ color: "#999" }}>Not provided</em>
                                          )}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* Business Information Card */}
                              <Grid item xs={12}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: "flex", alignItems: "center" }}>
                                      <AccountBalanceIcon sx={{ mr: 1, fontSize: 20 }} />
                                      Business Information
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={4}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <AccountBalanceIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Chamber of Commerce
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {entity.coC || <em style={{ color: "#999" }}>Not provided</em>}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <ReceiptIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            VAT
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {entity.vat || <em style={{ color: "#999" }}>Not provided</em>}
                                        </Typography>
                                      </Grid>
                                      {isGermanyCountry(entity.countryId, countries) || entity.steuerNummer ? (
                                        <Grid item xs={12} md={4}>
                                          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                            <ReceiptIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                              Steuernummer
                                            </Typography>
                                          </Box>
                                          <Typography variant="body1" sx={{ ml: 3 }}>
                                            {entity.steuerNummer || <em style={{ color: "#999" }}>Not provided</em>}
                                          </Typography>
                                        </Grid>
                                      ) : null}
                                      <Grid item xs={12} md={4}>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                          <GavelIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Tax Office
                                          </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ ml: 3 }}>
                                          {entity.taxOffice || <em style={{ color: "#999" }}>Not provided</em>}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                        No entities available
                      </Typography>
                    </CardContent>
                  </Card>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdf}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{pdfFileName || "PDF Document"}</DialogTitle>
        <DialogContent sx={{ height: "80vh" }}>
          {pdfLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pdfError ? (
            <Typography color="error">{pdfError}</Typography>
          ) : pdfUrl ? (
            <Box sx={{ height: "100%" }}>
              <iframe
                title="PDF preview"
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: "none" }}
              />
            </Box>
          ) : (
            <Typography color="text.secondary">No document available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdf}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Note Confirmation Dialog */}
      <Dialog
        open={deleteNoteDialogOpen}
        onClose={handleCancelDeleteNote}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the note <strong>"{noteToDelete?.shortDescription || "Untitled Note"}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteNote} disabled={deletingNote}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteNote}
            color="error"
            variant="contained"
            disabled={deletingNote}
            startIcon={deletingNote ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingNote ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConvertProspectEntitiesDialog
        open={convertDialogOpen}
        onClose={() => {
          setConvertDialogOpen(false);
        }}
        onConverted={fetchProspectDetails}
        prospectId={prospectId}
        prospectName={prospectDetails?.companyName}
      />
    </React.Fragment>
  );
};

export default ViewProspectManagementDialog;
